import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { removeTagsAndDecimalsFromFileName } from '../shared/statelessMethods';

/**
 * @typedef {object} XlsColumnType
 * @property {number} index column index, starting with 0 (Column A index = 0, B = 1, etc)
 * @property {XlsType} type
 * */

/**
 * @typedef {object} XlsSettings
 * @property {XlsColumnType[]} columnTypes
 * */

const EXTENTIONS = {
    excel: 'xlsx',
    csv: 'csv'
};

/**
 * @typedef {string} XlsType
 * @enum {XlsType}
 * */
const XlsType = Object.freeze({
    Number: 'Number',
});

/**
 * @param {string} fileName
 * @param {'excel' | 'csv'} type
 * @param {boolean} disableFileSave
 * @param {object[]} rows
 * @param {object} titleRow TODO check type here
 * @param {string[]} [ignoreColumns = ['cartID', 'currency']]
 * @param {XlsColumnType[]} columnTypes
 * @param {boolean} rtl
 * @return {Blob}
 * */
export function exportToExcel(
    fileName='report',
    type = 'excel',
    disableFileSave = false,
    rows,
    titleRow,
    ignoreColumns = ['cartID', 'currency'],
    columnTypes,
    rtl
) {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.' + EXTENTIONS[type];
    const filteredRows = rows
        .map(r => Object.entries(r).filter(c => ignoreColumns.indexOf(c[0]) < 0))
        .map(entries => Object.fromEntries(entries));
    const jsonRows = [...titleRow, ...filteredRows];
    return getFileBlobWithData(jsonRows, removeTagsAndDecimalsFromFileName(fileName), fileType, fileExtension, disableFileSave, columnTypes, rtl)
}

/**
 * @param jsonRows
 * @param fileName
 * @param fileType
 * @param fileExtension
 * @param {boolean} disableFileSave
 * @param {XlsColumnType[]} [columnTypes]
 * @param RTL
 * @return {Blob}
 * */
function getFileBlobWithData(jsonRows, fileName, fileType, fileExtension, disableFileSave = false, columnTypes, RTL = true) {
    const ws = XLSX.utils.json_to_sheet(jsonRows, { skipHeader: true });
    let rtlOptions = {Views: [{RTL}]};
    // if we need some columns to have numbers format in excel we need to pass here these columns letters as an array
    if (columnTypes != null) {
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let rowNum = range.s.r + 1; rowNum <= range.e.r; rowNum++) {
            for (const column of columnTypes) {
                const cellRef = XLSX.utils.encode_cell({ r: rowNum, c: column.index });
                const cell = ws[cellRef];
                // NOTE: cell.forced is a custom field you may use to prevent formatting for specific cells
                if (cell != null && Object.isExtensible(cell) && !cell.forced) {
                    switch (column.type) {
                        case 'Number':
                            cell.t = 'n'; // setting cell type as Number
                            cell.z = '0.00'; // setting cell format (z) to 0.00
                            break;
                        default:
                            cell.t = 't'; // setting cell type as Text by default
                    }
                }
            }
        }
    }
    const wb = { Sheets: { 'data': ws }, SheetNames: ['data'], Workbook: rtlOptions};
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    if (!disableFileSave) {
        FileSaver.saveAs(data, fileName + fileExtension);
    }
    return data;
};
